import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "../config";
import {
  Building2, Globe, MapPin, TrendingDown, AlertTriangle,
  Sparkles, ArrowRight, FileText, Target,
} from "lucide-react";
import NotFoundPage from "./NotFoundPage";
import { trackEvent } from "../lib/analytics";

/**
 * V231.4 — Programmatic SEO landing: /firma/:ico/ai-seo-scan.
 *
 * Handles ~700k SK firms on-demand. Fetches ORSF-backed data from
 * /api/firm-pages/{ico}, renders unique AI SEO scorecard + schema.org
 * Organization markup. CTA funnels to €14 Competitor Benchmark.
 */
export default function FirmAiSeoPage() {
  const { ico } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    setData(null);
    axios
      .get(`${API}/firm-pages/${ico}`)
      .then((r) => {
        setData(r.data);
        trackEvent("view_firm_ai_seo", { ico, source: "firm_page" });
      })
      .catch((err) => {
        if (err?.response?.status === 404 || !err?.response) setError(true);
        else console.error(err);
      });
  }, [ico]);

  // Override baked-in canonical from index.html with firm-specific URL.
  useEffect(() => {
    if (!data?.canonical_url) return;
    const el = document.querySelector('link[rel="canonical"]');
    const prev = el?.getAttribute("href");
    if (el) el.setAttribute("href", data.canonical_url);
    return () => {
      if (el && prev) el.setAttribute("href", prev);
    };
  }, [data?.canonical_url]);

  if (error) return <NotFoundPage />;
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0F2E] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const m = data.metrics;
  const title = `${data.name} — AI SEO scan · ${m.ai_seo_score}/100 · ChatGPT citations ${m.chatgpt_citations_est}/mes`;
  const description = `Zisti ako ${data.name} (IČO ${data.ico}) vyzerá v AI vyhľadávačoch. ChatGPT ${m.chatgpt_citations_est} citácií/mes · Perplexity ${m.perplexity_citations_est} · ${m.fixable_issues} opraviteľných chýb na doméne ${data.domain}.`;

  const org = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: data.name,
    identifier: data.ico,
    address: { "@type": "PostalAddress", streetAddress: data.address, addressCountry: data.country },
    url: `https://${data.domain}`,
    sameAs: [data.canonical_url],
  };
  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Domov", item: "https://toprankpulse.com/" },
      { "@type": "ListItem", position: 2, name: "AI SEO firmy", item: "https://toprankpulse.com/firma" },
      { "@type": "ListItem", position: 3, name: data.name, item: data.canonical_url },
    ],
  };

  return (
    <main className="min-h-screen bg-[#0A0F2E] text-white" data-testid="firm-ai-seo-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={data.canonical_url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={data.canonical_url} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(org)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
      </Helmet>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-10" data-testid="firm-hero">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-400 font-semibold mb-4">
          <Building2 className="w-3.5 h-3.5" />
          AI SEO scorecard · IČO {data.ico}
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-4">
          {data.name}
        </h1>
        <div className="flex flex-wrap gap-4 text-sm text-slate-400 mb-6">
          <span className="inline-flex items-center gap-1.5">
            <Globe className="w-4 h-4" /> {data.domain}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-4 h-4" /> {data.address}
          </span>
          {data.nace && (
            <span className="inline-flex items-center gap-1.5">
              <FileText className="w-4 h-4" /> NACE {data.nace}
            </span>
          )}
        </div>
        <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-7">
          <strong className="text-white">{data.name}</strong> má aktuálne AI SEO score{" "}
          <span className="text-amber-300 font-bold">{m.ai_seo_score}/100</span> a odhadovaných{" "}
          <strong className="text-white">{m.chatgpt_citations_est} ChatGPT citácií/mes</strong>.
          V porovnaní s priemerom odvetvia{" "}
          <span className={m.rank_vs_industry_benchmark >= 0 ? "text-emerald-400 font-semibold" : "text-rose-400 font-semibold"}>
            {m.rank_vs_industry_benchmark >= 0 ? "+" : ""}{m.rank_vs_industry_benchmark} b.
          </span>
          {" "}·{" "}<span className="text-white font-semibold">{m.fixable_issues} opraviteľných chýb</span> na doméne {data.domain}.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/benchmark"
            data-testid="firm-cta-benchmark"
            className="bg-amber-400 hover:bg-amber-300 text-black font-bold px-7 py-3.5 rounded-lg transition inline-flex items-center gap-2"
          >
            Získaj plný benchmark pre {data.domain} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/snapshot"
            data-testid="firm-cta-snapshot"
            className="border border-white/20 hover:border-white/40 text-white font-semibold px-7 py-3.5 rounded-lg transition"
          >
            Rýchly scan za €7 →
          </Link>
        </div>
      </section>

      {/* AI citation estimates */}
      <section className="max-w-5xl mx-auto px-6 pb-10" data-testid="firm-citations">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">
          Odhadované AI citácie / mes
        </h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <CitationCard engine="ChatGPT" count={m.chatgpt_citations_est} color="emerald" />
          <CitationCard engine="Perplexity" count={m.perplexity_citations_est} color="violet" />
          <CitationCard engine="Gemini" count={m.gemini_citations_est} color="sky" />
        </div>
      </section>

      {/* Weak spot + Recommendation */}
      <section className="max-w-5xl mx-auto px-6 py-10 grid md:grid-cols-2 gap-4" data-testid="firm-insights">
        <div className="bg-rose-500/5 border border-rose-500/20 rounded-xl p-6">
          <div className="flex items-center gap-2 text-rose-400 text-xs uppercase tracking-widest font-semibold mb-3">
            <AlertTriangle className="w-4 h-4" />
            Najväčšia slabina
          </div>
          <p className="text-lg text-white leading-relaxed">
            Doména <strong>{data.domain}</strong>: <span className="text-rose-200">{m.top_weak_spot}</span>.
          </p>
        </div>
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-6">
          <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest font-semibold mb-3">
            <Target className="w-4 h-4" />
            Odporúčaná akcia
          </div>
          <p className="text-lg text-white leading-relaxed">
            {m.top_recommendation}.
          </p>
        </div>
      </section>

      {/* Metrics strip */}
      <section className="max-w-5xl mx-auto px-6 py-6" data-testid="firm-metrics">
        <div className="grid sm:grid-cols-4 gap-3">
          <MetricCard icon={TrendingDown} label="AI SEO Score" value={`${m.ai_seo_score}/100`} />
          <MetricCard icon={Target} label="vs. odvetvie" value={`${m.rank_vs_industry_benchmark >= 0 ? "+" : ""}${m.rank_vs_industry_benchmark} b.`} />
          <MetricCard icon={Sparkles} label="Fixable issues" value={m.fixable_issues} />
          <MetricCard icon={FileText} label="Celkom AI citations" value={m.chatgpt_citations_est + m.perplexity_citations_est + m.gemini_citations_est} />
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="firm-closing-cta">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5">
          Chceš plný AI SEO benchmark pre <span className="text-amber-300">{data.name}</span>?
        </h2>
        <p className="text-slate-300 mb-7 leading-relaxed">
          €14 Benchmark report porovná {data.domain} s 3 top konkurentmi v NACE odvetví a doručí PDF do 10 minút na tvoj email.
        </p>
        <Link
          to="/benchmark"
          data-testid="firm-closing-benchmark"
          className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-bold px-8 py-4 rounded-lg transition"
        >
          Objednať benchmark pre {data.domain} za €14 <ArrowRight className="w-4 h-4" />
        </Link>
        <p className="mt-5 text-xs text-slate-500">
          Dáta pochádzajú z ORSF.sk (Register právnických osôb) + proprietárneho AI SEO modelu TopRankPulse.
        </p>
      </section>
    </main>
  );
}

function CitationCard({ engine, count, color }) {
  const map = {
    emerald: "text-emerald-400 border-emerald-500/20",
    violet: "text-violet-400 border-violet-500/20",
    sky: "text-sky-400 border-sky-500/20",
  };
  return (
    <div className={`bg-[#111634] border rounded-xl p-5 ${map[color]}`}>
      <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{engine}</div>
      <div className="text-4xl font-bold tabular-nums">{count}</div>
      <div className="text-xs text-slate-500 mt-1">citácií / mes</div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#111634] border border-slate-700 rounded-xl p-4">
      <Icon className="w-4 h-4 text-slate-400 mb-2" />
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
